import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/CarSettlementDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { clampDateString } from "../utils/dateUtils";
import { getCarNumberMatchKey } from "../utils/dispatchConflictUtils";

const number = (v) => (Number(v) || 0).toLocaleString();
const normalizeText = (value = "") => String(value || "").trim();
const last4 = (value = "") => String(value || "").replace(/\D/g, "").slice(-4);
const normalizeCarKey = (value = "") => getCarNumberMatchKey(value) || normalizeText(value);
const getExpenseMonth = (row = {}) => String(row.date || "").slice(0, 7);
const isInstallmentAutoExpense = (row = {}) => String(row.bigo || "").startsWith("할부 내역 자동 등록");
const isGeneralAutomationExpense = (row = {}) => String(row.bigo || "").startsWith("지출 자동화");
const getMonthlyCarExpenseKey = (row = {}) => `${normalizeCarKey(row.car_number)}__${getExpenseMonth(row)}`;

async function fetchAllPages(buildQuery, pageSize = 1000) {
  const rows = [];

  for (let from = 0; ; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;

    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }

  return rows;
}

// 지출 카테고리 묶음
const EXPENSE_BUCKETS = {
  인건비: ["geubyeo", "4bohum"],
  유류비: ["jooyu"],
  정비비: ["jungbi"],
  할부: ["halbu"],
  보험료: ["bohum"],
  운영비용: ["tonghaeng", "joocha", "secha", "sooksic", "chooljang"],
  제세공과금: ["johab", "unyoung", "gps", "gwatae"],
  기타: ["gita"],
};

// 예약 타입 → 카테고리 매핑(알 수 없는 값은 ‘기타’)
const mapReservationType = (raw) => {
  const t = (raw || "").toString().trim();
  if (["일반", "일반예약", "general", "normal"].includes(t)) return "일반";
  if (["통근", "commute"].includes(t)) return "통근";
  if (["학단", "school", "academy"].includes(t)) return "학단";
  if (["투어", "tour"].includes(t)) return "투어";
  return "기타";
};

export default function CarSettlementDetail() {
  const { carNumber } = useParams(); // 경로의 차량번호
  const navigate = useNavigate();
  const { search, state } = useLocation();

  const qs = new URLSearchParams(search);
  const from = qs.get("from") || "";
  const to = qs.get("to") || "";
  const initialSalesFrom = qs.get("salesFrom") || from;
  const initialSalesTo = qs.get("salesTo") || to;
  const initialExpenseFrom = qs.get("expenseFrom") || from;
  const initialExpenseTo = qs.get("expenseTo") || to;

  const company = localStorage.getItem("email") || "";

  // 상단 요약(목록에서 넘겨준 기사명만 초기 표시용으로 사용)
  const passed = {
    human_name: state?.human_name || "",
  };

  const [loading, setLoading] = useState(false);
  const [driver, setDriver] = useState(passed.human_name);
  const [settlementCarOptions, setSettlementCarOptions] = useState([]);
  const [salesRange, setSalesRange] = useState({ from: initialSalesFrom, to: initialSalesTo });
  const [expenseRange, setExpenseRange] = useState({ from: initialExpenseFrom, to: initialExpenseTo });
  const [salesBuckets, setSalesBuckets] = useState({
    일반: 0, 통근: 0, 학단: 0, 투어: 0, 기타: 0, total: 0,
  });
  const [expenseBuckets, setExpenseBuckets] = useState(
    Object.fromEntries(Object.keys(EXPENSE_BUCKETS).map(k => [k, 0]))
  );
  const expenseTotal = useMemo(
    () => Object.values(expenseBuckets).reduce((a, b) => a + Number(b || 0), 0),
    [expenseBuckets]
  );
  const handlePrint = () => {
    window.print();
  };

  const handleSalesRangeChange = (field, value) => {
    setSalesRange((prev) => ({ ...prev, [field]: clampDateString(value) }));
  };

  const handleExpenseRangeChange = (field, value) => {
    setExpenseRange((prev) => ({ ...prev, [field]: clampDateString(value) }));
  };

  const navigateToCar = (nextCarNumber) => {
    if (!nextCarNumber || nextCarNumber === carNumber) return;
    const option = settlementCarOptions.find((item) => item.car_number === nextCarNumber);
    const params = new URLSearchParams();
    params.set("from", salesRange.from || "");
    params.set("to", salesRange.to || "");
    params.set("salesFrom", salesRange.from || "");
    params.set("salesTo", salesRange.to || "");
    params.set("expenseFrom", expenseRange.from || "");
    params.set("expenseTo", expenseRange.to || "");
    navigate(`/settlements/${encodeURIComponent(nextCarNumber)}?${params.toString()}`, {
      state: { human_name: option?.human_name || "" },
    });
  };

  // 데이터 조회(기간/회사/차량번호 기준)
  const fetchAll = async () => {
    if (!company || !carNumber) return;
    setLoading(true);
    try {
      setDriver("");
      const { data: carRows, error: carErr } = await supabase
        .from("cars")
        .select("driver, car_number, car_number_only")
        .eq("company", company);
      if (carErr) throw carErr;

      const driversMap = {};
      (carRows || []).forEach((car) => {
        if (car?.car_number) driversMap[normalizeCarKey(car.car_number)] = normalizeText(car.driver);
        if (car?.car_number_only) driversMap[normalizeCarKey(car.car_number_only)] = normalizeText(car.driver);
      });

      const driverForCar = (value) => driversMap[normalizeCarKey(value)] || "";

      const targetCarKey = normalizeCarKey(carNumber);
      const carMatch = (carRows || []).find((car) =>
        normalizeCarKey(car.car_number) === targetCarKey ||
        normalizeCarKey(car.car_number_only) === targetCarKey ||
        last4(car.car_number_only) === last4(carNumber) ||
        last4(car.car_number) === last4(carNumber)
      );
      const targetDriver = normalizeText(carMatch?.driver || passed.human_name);
      const targetDriverKey = normalizeText(targetDriver);
      const matchesTargetDriver = (row) =>
        !targetDriverKey ||
        (normalizeText(row.human_name) || driverForCar(row.car_number)) === targetDriverKey;

      // 1) 매출(예약): 목록 화면과 같은 기준으로 차량번호를 정규화해서 매칭
      const salesRows = await fetchAllPages(() => {
        let query = supabase
          .from("reservations")
          .select("id, date, car_number, money, reservation_type, human_name, company")
          .eq("company", company)
          .order("date", { ascending: true })
          .order("id", { ascending: true });
        if (salesRange.from) query = query.gte("date", salesRange.from);
        if (salesRange.to) query = query.lte("date", salesRange.to);
        return query;
      });
      const expenseRows = await fetchAllPages(() => {
        let query = supabase
          .from("cars-expenses")
          .select("*")
          .eq("company", company)
          .order("date", { ascending: true })
          .order("id", { ascending: true });
        if (expenseRange.from) query = query.gte("date", expenseRange.from);
        if (expenseRange.to) query = query.lte("date", expenseRange.to);
        return query;
      });

      const optionMap = new Map();
      const addCarOption = (row) => {
        const rowCarNumber = normalizeText(row?.car_number);
        if (!rowCarNumber) return;
        const key = normalizeCarKey(rowCarNumber);
        if (!key || optionMap.has(key)) return;
        optionMap.set(key, {
          car_number: rowCarNumber,
          human_name: normalizeText(row?.human_name) || driverForCar(rowCarNumber),
        });
      };
      salesRows.forEach(addCarOption);
      expenseRows.forEach(addCarOption);
      if (!optionMap.has(targetCarKey)) {
        optionMap.set(targetCarKey, {
          car_number: carNumber,
          human_name: targetDriver,
        });
      }
      setSettlementCarOptions(
        Array.from(optionMap.values()).sort((a, b) =>
          String(a.car_number).localeCompare(String(b.car_number), "ko-KR", { numeric: true })
        )
      );

      const sales = salesRows.filter(
        (row) =>
          normalizeCarKey(row.car_number) === targetCarKey &&
          matchesTargetDriver(row)
      );

      // 2) 지출: 목록 화면과 같은 기준으로 차량번호를 정규화해서 매칭
      const exps = expenseRows.filter(
        (row) =>
          normalizeCarKey(row.car_number) === targetCarKey &&
          matchesTargetDriver(row)
      );
      const installmentAutoExpenseKeys = new Set(
        exps
          .filter((row) => isInstallmentAutoExpense(row) && Number(row.halbu || 0) > 0)
          .map(getMonthlyCarExpenseKey)
      );

      setDriver(targetDriver);

      // 매출 버킷 집계
      const salesAgg = { 일반: 0, 통근: 0, 학단: 0, 투어: 0, 기타: 0, total: 0 };
      for (const s of sales || []) {
        const bucket = mapReservationType(s.reservation_type);
        const salesAmount = Number(s.money) || 0;
        salesAgg[bucket] += salesAmount;
        salesAgg.total += salesAmount;
      }

      // 지출 버킷 집계
      const expAgg = Object.fromEntries(Object.keys(EXPENSE_BUCKETS).map(k => [k, 0]));
      for (const e of exps || []) {
        for (const [bucket, keys] of Object.entries(EXPENSE_BUCKETS)) {
          for (const k of keys) {
            if (
              k === "halbu" &&
              isGeneralAutomationExpense(e) &&
              installmentAutoExpenseKeys.has(getMonthlyCarExpenseKey(e))
            ) {
              continue;
            }
            expAgg[bucket] += Number(e[k]) || 0;
          }
        }
      }
      setSalesBuckets(salesAgg);
      setExpenseBuckets(expAgg);
    } catch (e) {
      console.error(e);
      showUserAlert("차량 정산 상세 조회 중 오류: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carNumber, salesRange.from, salesRange.to, expenseRange.from, expenseRange.to]);

  // 최종 요약(넘겨받은 값이 있으면 우선 표기하되, 실제 계산값으로 교체)
  const summary = useMemo(() => {
    const sales = salesBuckets.total || 0;
    const expense = expenseTotal || 0;
    const net = sales - expense;
    return { sales, expense, net };
  }, [salesBuckets.total, expenseTotal]);
  const carSelectOptions = settlementCarOptions.length > 0
    ? settlementCarOptions
    : [{ car_number: carNumber, human_name: driver }];

  return (
    <div className="space-y-4">
      <Header />

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between no-print">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">정산내역 -</h2>
            <select
              value={carNumber}
              onChange={(event) => navigateToCar(event.target.value)}
              className="h-9 rounded border px-2 text-sm font-semibold text-blue-700"
              disabled={loading}
            >
              {carSelectOptions.map((option) => (
                <option key={`${option.car_number}-${option.human_name}`} value={option.car_number}>
                  {option.car_number}
                  {option.human_name ? ` (${option.human_name})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>인쇄</Button>
            <Button variant="outline" onClick={() => navigate(-1)}>뒤로</Button>
          </div>
        </div>

        {/* 🖨️ 프린트 전용 머리말 */}
        <div className="print-only mb-3">
          <div className="text-xl font-bold">
            정산내역 - <span className="text-blue-700">{carNumber}</span>
          </div>
          <div className="text-sm text-gray-700">
            매출 기간: {salesRange.from || "-"} ~ {salesRange.to || "-"}
          </div>
          <div className="text-sm text-gray-700">
            지출 기간: {expenseRange.from || "-"} ~ {expenseRange.to || "-"}
          </div>
          <div className="text-sm text-gray-700">
            기사명: {driver || "-"}
          </div>
        </div>

        {/* 상단 기본 정보 */}
        <Card>
          <CardContent className="bg-white space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-gray-500">차량번호</div>
                <select
                  value={carNumber}
                  onChange={(event) => navigateToCar(event.target.value)}
                  className="mt-1 h-9 w-full rounded border px-2 font-semibold text-blue-700 no-print"
                  disabled={loading}
                >
                  {carSelectOptions.map((option) => (
                    <option key={`card-${option.car_number}-${option.human_name}`} value={option.car_number}>
                      {option.car_number}
                      {option.human_name ? ` (${option.human_name})` : ""}
                    </option>
                  ))}
                </select>
                <div className="print-only font-semibold">{carNumber}</div>
              </div>
              <div>
                <div className="text-gray-500">기사명</div>
                <div className="font-semibold">{driver || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">매출/지출 기간</div>
                <div className="font-semibold">
                  {salesRange.from || "-"} ~ {salesRange.to || "-"} / {expenseRange.from || "-"} ~ {expenseRange.to || "-"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
              <div className="p-2 rounded bg-emerald-50">
                <div className="text-gray-600">매출합</div>
                <div className="text-lg font-bold text-emerald-700">{number(summary.sales)}원</div>
              </div>
              <div className="p-2 rounded bg-rose-50">
                <div className="text-gray-600">지출합</div>
                <div className="text-lg font-bold text-rose-700">{number(summary.expense)}원</div>
              </div>
              <div className="p-2 rounded bg-indigo-50">
                <div className="text-gray-600">합계(매출-지출)</div>
                <div className="text-lg font-bold text-indigo-700">{number(summary.net)}원</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 내역서: 좌/우 2단 배치 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 매출 내역 */}
          <Card>
            <CardContent className="bg-white space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold">매출 내역</div>
                <div className="flex flex-wrap items-center gap-2 no-print">
                  <Input
                    type="date"
                    value={salesRange.from}
                    onChange={(e) => handleSalesRangeChange("from", e.target.value)}
                    className="w-[140px]"
                  />
                  <span className="text-sm">~</span>
                  <Input
                    type="date"
                    value={salesRange.to}
                    onChange={(e) => handleSalesRangeChange("to", e.target.value)}
                    className="w-[140px]"
                  />
                </div>
              </div>
              <table className="table-auto w-full border text-sm print-table">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left">구분</th>
                    <th className="border p-2 text-right">금액</th>
                  </tr>
                </thead>
                <tbody>
                  {["일반","통근","학단","투어","기타"].map((k) => (
                    <tr key={k}>
                      <td className="border p-2">{k}</td>
                      <td className="border p-2 text-right">{number(salesBuckets[k])}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="border p-2">합계</td>
                    <td className="border p-2 text-right">{number(salesBuckets.total)}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* 지출 내역 */}
          <Card>
            <CardContent className="bg-white space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold">지출 내역</div>
                <div className="flex flex-wrap items-center gap-2 no-print">
                  <Input
                    type="date"
                    value={expenseRange.from}
                    onChange={(e) => handleExpenseRangeChange("from", e.target.value)}
                    className="w-[140px]"
                  />
                  <span className="text-sm">~</span>
                  <Input
                    type="date"
                    value={expenseRange.to}
                    onChange={(e) => handleExpenseRangeChange("to", e.target.value)}
                    className="w-[140px]"
                  />
                </div>
              </div>
              <table className="table-auto w-full border text-sm print-table">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left">구분</th>
                    <th className="border p-2 text-right">금액</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(EXPENSE_BUCKETS).map((k) => (
                    <tr key={k}>
                      <td className="border p-2">{k}</td>
                      <td className="border p-2 text-right">{number(expenseBuckets[k])}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="border p-2">합계</td>
                    <td className="border p-2 text-right">{number(expenseTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {loading && <div className="text-sm text-gray-500">불러오는 중...</div>}
      </div>
    </div>
  );
}
